import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Star,
  FileText,
  Briefcase,
  MessageSquare,
  AlertTriangle,
  Mail,
  Settings,
  Save,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

interface NotificationPreferencesProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationPreferences({ isOpen, onClose }: NotificationPreferencesProps) {
  const [isSaving, setIsSaving] = useState(false);

  // Get current preferences
  const preferences = useQuery(api.notificationPreferences.getUserNotificationPreferences, {});
  const updatePreferences = useMutation(api.notificationPreferences.updateNotificationPreferences);

  // Local state for form
  const [formData, setFormData] = useState({
    reviewNotificationsEnabled: preferences?.reviewNotificationsEnabled ?? true,
    reviewNotificationMinRating: preferences?.reviewNotificationMinRating ?? 1,
    documentNotificationsEnabled: preferences?.documentNotificationsEnabled ?? true,
    messageNotificationsEnabled: preferences?.messageNotificationsEnabled ?? true,
    systemNotificationsEnabled: preferences?.systemNotificationsEnabled ?? true,
    emailNotificationsEnabled: preferences?.emailNotificationsEnabled ?? true,
    notificationFrequency: preferences?.notificationFrequency ?? "immediate",
  });

  // Update form data when preferences load
  React.useEffect(() => {
    if (preferences) {
      setFormData({
        reviewNotificationsEnabled: preferences.reviewNotificationsEnabled,
        reviewNotificationMinRating: preferences.reviewNotificationMinRating || 1,
        documentNotificationsEnabled: preferences.documentNotificationsEnabled,
        messageNotificationsEnabled: preferences.messageNotificationsEnabled,
        systemNotificationsEnabled: preferences.systemNotificationsEnabled,
        emailNotificationsEnabled: preferences.emailNotificationsEnabled,
        notificationFrequency: preferences.notificationFrequency,
      });
    }
  }, [preferences]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updatePreferences(formData);
      toast.success("Notification preferences updated successfully");
      onClose();
    } catch (error) {
      console.error("Failed to update preferences:", error);
      toast.error("Failed to update notification preferences");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="max-h-[90vh] w-full max-w-2xl overflow-y-auto">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              <CardTitle>Notification Preferences</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Review Notifications */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              <h3 className="font-medium">Review Notifications</h3>
              <Badge variant="secondary">New</Badge>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label>Enable review notifications</Label>
                <p className="text-sm text-gray-600">Get notified when clients submit reviews</p>
              </div>
              <Switch
                checked={formData.reviewNotificationsEnabled}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, reviewNotificationsEnabled: checked }))
                }
              />
            </div>

            {formData.reviewNotificationsEnabled && (
              <div className="ml-4 space-y-2">
                <Label>Minimum rating to notify</Label>
                <Select
                  value={formData.reviewNotificationMinRating.toString()}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      reviewNotificationMinRating: parseInt(value),
                    }))
                  }
                >
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1+ stars (all reviews)</SelectItem>
                    <SelectItem value="2">2+ stars</SelectItem>
                    <SelectItem value="3">3+ stars</SelectItem>
                    <SelectItem value="4">4+ stars</SelectItem>
                    <SelectItem value="5">5 stars only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator />

          {/* Message Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-green-500" />
              <div className="space-y-1">
                <Label>Message notifications</Label>
                <p className="text-sm text-gray-600">New messages and conversations</p>
              </div>
            </div>
            <Switch
              checked={formData.messageNotificationsEnabled}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, messageNotificationsEnabled: checked }))
              }
            />
          </div>

          {/* Document Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <div className="space-y-1">
                <Label>Document notifications</Label>
                <p className="text-sm text-gray-600">Document expiration reminders</p>
              </div>
            </div>
            <Switch
              checked={formData.documentNotificationsEnabled}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, documentNotificationsEnabled: checked }))
              }
            />
          </div>

          {/* System Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-500" />
              <div className="space-y-1">
                <Label>System notifications</Label>
                <p className="text-sm text-gray-600">System announcements and updates</p>
              </div>
            </div>
            <Switch
              checked={formData.systemNotificationsEnabled}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, systemNotificationsEnabled: checked }))
              }
            />
          </div>

          <Separator />

          {/* Email Notifications */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <div className="space-y-1">
                <Label>Email notifications</Label>
                <p className="text-sm text-gray-600">Receive notifications via email</p>
              </div>
            </div>
            <Switch
              checked={formData.emailNotificationsEnabled}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, emailNotificationsEnabled: checked }))
              }
            />
          </div>

          {/* Notification Frequency */}
          <div className="space-y-2">
            <Label>Notification frequency</Label>
            <Select
              value={formData.notificationFrequency}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, notificationFrequency: value as any }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="hourly">Hourly digest</SelectItem>
                <SelectItem value="daily">Daily digest</SelectItem>
                <SelectItem value="weekly">Weekly digest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Preferences
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
